// src/templates/family-contact.js - Contact page with i18n support
import React, { useState, useEffect } from "react"
import { graphql, Link } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import SEO from "../components/seo"
import AdoptionHeader from "../components/AdoptionHeader"
import AdoptionFooter from "../components/AdoptionFooter"
import ContactForm from "../components/contact-form"

const FamilyContact = ({ data, pageContext }) => {
  const { t } = useTranslation()
  const family = data.account
  const { familyWebsite } = pageContext

  // Get IDs for API submission
  const accountId = family?.mongoId
  const agencyId = family?.agency?.mongoId

  if (!family) {
    return (
      <div>
        <AdoptionHeader />
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="text-center">
            <h1 className="mb-4 text-4xl font-bold text-gray-800 font-heading-main">
              {t('common.familyNotFound')}
            </h1>
            <p className="text-lg text-gray-600 font-body">
              {t('common.familyNotFoundMessage')}
            </p>
          </div>
        </div>
        <AdoptionFooter />
      </div>
    )
  }

  const familyName = family.name ? family.name.replace(' / ', ' and ') : "Adoptive Family"
  const displayName = familyName.replace(' Family', '').replace('The ', '')
  const agencyData = family?.agency || {}
  
  // Translation helpers for family type
  const familyTypeText = family.account_type === 'Couple' ? t('common.us') : t('common.me')

  // Generate comprehensive SEO data
  const pageTitle = t('contact.seoTitle', { familyName })
  const pageDescription = t('contact.seoDescription', { familyName })
  
  const keywords = [
    t('contact.seoKeywords.contact'),
    `${familyName}`,
    t('contact.seoKeywords.adoptiveParentsContact'),
    t('contact.seoKeywords.adoptionInquiry'),
    family.contactInfo?.state?.name && t('contact.seoKeywords.stateAdoption', { state: family.contactInfo.state.name }),
    t('contact.seoKeywords.birthParentSupport')
  ].filter(Boolean)

  // Get primary contact info
  let primaryPhone = process.env.PRIMARY_CONTACT_NUMBER ? ('+1' + process.env.PRIMARY_CONTACT_NUMBER.replace(/\D/g, '')) : '+13176527696'
  let textNumber = primaryPhone
  let callNumber = primaryPhone
  if (agencyData) {
    if (agencyData.bm_contact) {
      if (agencyData.bm_contact.text) {
        textNumber = `+1${agencyData.bm_contact.text.replace(/\D/g, '')}`
      }
      if (agencyData.bm_contact.phone) {
        callNumber = `+1${agencyData.bm_contact.phone.replace(/\D/g, '')}`
      }
    } else if (agencyData.phone?.length && agencyData.phone[0]?.number) {
      primaryPhone = (agencyData.phone[0].country_code ? agencyData.phone[0].country_code : '+1') + agencyData.phone[0].number.replace(/\D/g, '')
      textNumber = primaryPhone
      callNumber = primaryPhone
    }
  }
  let bmContactEmail = (process.env.PRIMARY_EMAIL || 'greg.phelps@cairsolutions.com')
  if (agencyData?.bm_contact?.email) {
    bmContactEmail = agencyData.bm_contact.email
  } else if (agencyData?.email?.length) {
    bmContactEmail = agencyData.email[0].email
  }

  // Contact methods with actions
  const handleCallNow = () => {
    window.location.href = `tel:${callNumber}`
  }

  const handleSendText = () => {
    window.location.href = `sms:${textNumber}?body=${t('contact.textMessage', { familyName })}`
  }

  const handleSendMail = () => {
    window.location.href = `mailto:${bmContactEmail}`
  }

  return (
    <div className="font-sans bg-gray-50" style={{ margin: 0, padding: 0 }}>
      <SEO
        title={pageTitle}
        description={pageDescription}
        pathname="/contact"
        keywords={keywords}
        familyData={family}
        image={family.avatar ? `/images/families/${family.avatar}` : null}
      />
      
      <AdoptionHeader
        family={family}
        agencyId={agencyId}
        accountId={accountId}
      />

      <main>
        {/* How Would You Like to Connect Section */}
        <div className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Section Title */}
            <div className="text-center mb-16">
              <h1 className="text-gray-600 text-4xl lg:text-5xl mb-8 font-heading-main">
                {t('contact.howToConnect')}
              </h1>
            </div>

            {/* Contact Method Cards - Updated to use Material Icons */}
            <div className="grid md:grid-cols-3 gap-8">
              
              {/* Call Us Anytime */}
              <div 
                className="rounded-3xl p-8 text-center text-gray-600"
                style={{ backgroundColor: '#9bd45a' }}
              >
                <div className="mb-6">
                  <span className="material-icons mx-auto block" style={{ fontSize: '48px' }}>
                    call
                  </span>
                </div>
                
                <h3 className="text-2xl font-semibold mb-4 font-heading-sub">
                  {t('contact.callAnytime.title')}
                </h3>
                
                <p className="mb-8 opacity-90 font-body">
                  {t('contact.callAnytime.description')}
                </p>
                
                <button
                  onClick={handleCallNow}
                  className="text-gray-600 px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-all duration-300 cursor-pointer transform hover:scale-105 border-2 border-gray-600 font-heading-sub"
                  aria-label={t('contact.callAnytime.button')}
                >
                  {t('contact.callAnytime.button')}
                </button>
              </div>

              {/* Text Message */}
              <div 
                className="rounded-3xl p-8 text-center text-gray-600"
                style={{ backgroundColor: '#9bd45a' }}
              >
                <div className="mb-6">
                  <span className="material-icons mx-auto block" style={{ fontSize: '48px' }}>
                    textsms
                  </span>
                </div>
                
                <h3 className="text-2xl font-semibold mb-4 font-heading-sub">
                  {t('contact.textMsg.title')}
                </h3>
                
                <p className="mb-8 opacity-90 font-body">
                  {t('contact.textMsg.description')}
                </p>
                
                <button
                  onClick={handleSendText}
                  className="text-gray-600 px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-all duration-300 transform cursor-pointer hover:scale-105 border-2 border-gray-600 font-heading-sub"
                  aria-label={t('contact.textMsg.button')}
                >
                  {t('contact.textMsg.button')}
                </button>
              </div>

              {/* Email Us */}
              <div 
                className="rounded-3xl p-8 text-center text-gray-600"
                style={{ backgroundColor: '#9bd45a' }}
              >
                <div className="mb-6">
                  <span className="material-icons mx-auto block" style={{ fontSize: '48px' }}>
                    mail
                  </span>
                </div>
                
                <h3 className="text-2xl font-semibold mb-4 font-heading-sub">
                  {t('contact.email.title')}
                </h3>
                
                <p className="mb-8 opacity-90 font-body">
                  {t('contact.email.description')}
                </p>
                
                <button
                  onClick={handleSendMail}
                  className="text-gray-600 px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-all duration-300 transform cursor-pointer hover:scale-105 border-2 border-gray-600 font-heading-sub"
                  aria-label={t('contact.email.button')}
                >
                  {t('contact.email.button')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Section - Now using the reusable component */}
        <ContactForm 
          accountId={accountId}
          agencyId={agencyId}
          familyName={familyName}
          familyType={family.account_type}
        />

        {/* Ready to Take the Next Step Section */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            <h2 className="text-gray-600 text-4xl lg:text-5xl mb-8 font-heading-main">
              {t('contact.nextStep.title')}
            </h2>
            
            <p className="text-lg text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto font-body">
              {t('contact.nextStep.description')}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleCallNow}
                className="text-gray-600 px-10 py-4 rounded-full font-semibold hover:opacity-90 transition-all duration-300 cursor-pointer transform hover:scale-105 shadow-lg text-lg font-heading-sub"
                style={{ backgroundColor: '#9bd45a' }}
                aria-label={t('contact.nextStep.callButton', { familyTypeText })}
              >
                {t('contact.nextStep.callButton', { familyTypeText: familyTypeText.toUpperCase() })}
              </button>
              <Link 
                to="/about"
                className="text-gray-600 px-10 py-4 rounded-full font-semibold hover:opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg text-lg inline-block font-heading-sub"
                style={{ backgroundColor: '#9bd45a' }}
              >
                {t('contact.nextStep.learnButton', { familyTypeText: familyTypeText.toUpperCase() })}
              </Link>
            </div>
          </div>
        </div>

      </main>

      <AdoptionFooter family={family} />
    </div>
  )
}

export const Head = ({ data }) => (
  <SEO title={`Contact ${data.account?.name || 'Our Family'} - Get In Touch`} />
)

export const query = graphql`
  query($accountId: String!, $language: String!) {
    account(id: { eq: $accountId }) {
      id
      mongoId
      name
      status
      account_type
      waiting_for
      contactInfo {
        website
        state {
          name
        }
      }
      avatar
      banner_photo
      agency {
        mongoId
        name
        address {
          line_1
          line_2
          street
          city
          state {
            name
          }
          zip
        }
        email {
          email
        }
        phone {
          number
        }
        bm_contact {
          email
          phone
          text
        }
        website
        avatar
      }
    }
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

export default FamilyContact